import React, { useEffect, useState, Fragment } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useMutation } from '@apollo/client';
import _ from 'lodash';

import {
    StyledCard,
    StyledCardContent,
    Text,
    AvatarWithUploader,
    Select,
    InputWithEndAdornment,
    RadioGroup,
    Radio,
    RadioCard,
    PhoneFormat,
    Input,
    OutlinedButton,
    ContainedButton,
    ConfirmAccountDialog,
    TagAdd,
    Progress,
    PasswordInput,
    AddMoreInput,
    ConfirmDialog,
    StyledFormHelperText,
} from '../../index';

import {
    GET_TITLES,
    GET_POSITIONS,
    GET_ROLES,
    GET_PROVIDER,
    UPDATE_PROVIDER,
    DELETE_USER_AVATAR,
    addProviderByStaff,
    deactivateStaff,
    checkLastAdmin,
    deleteStaff,
    checkExistUser,
    addExistUserToStaff,
} from '../../../services/provider';
import { deleteMedia, uploadMedia, resetPassword } from '../../../services/index';
import { alertError, alertSuccess } from '../../../redux/stateActions';
import getFileFromUrl from '../../../utils/getFileFromUrl';
import { validEmail, validPhone, specialCharacters, validPassword } from '../../../utils/regex';
import { fetchUser } from '../../../redux/userAction';

const PersonalProfile = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { id } = useParams();
    const user = useSelector((state) => state.user);

    const [showDialog, setShowDialog] = useState(false);

    const [errors, setErrors] = useState([]);
    const [inputValue, setInputValue] = useState({
        avatar: '',
        title1: '',
        title2: '',
        fullname: '',
        gender: '',
        specialist: [],
        position: '',
        role: '',
        email: '',
        mobileNumber: '',
        workingExperiences: [''],
        educationCertificates: [''],
    });

    const [deactivated, setDeactivated] = useState(false);

    const [loading, setLoading] = useState(false);
    const [parentReset, setParentReset] = useState(0);

    const handleCheckLastAdmin = async () => {
        const newErrors = [...errors];

        try {
            const res = await checkLastAdmin({
                hospitalID: getProviderData.staff.hospital.id,
                staffID: getProviderData.staff.id,
            });

            if (res.status === 200) {
                if (res.data.canChange === false) {
                    newErrors.push({
                        property: 'role',
                        message: 'Cannot change role to provider',
                    });
                }
            } else {
                throw new Error(res);
            }
        } catch (error) {
            newErrors.push({
                property: 'role',
                message: error.response.data.message,
            });
        }
        setErrors(newErrors);
    };

    const handleChange = (e) => {
        const newInputValue = { ...inputValue };
        const name = e.target.name;
        let value = e.target.value;
        if ((name === 'title1' || name === 'title2') && value === 'none') {
            value = '';
        }

        newInputValue[name] = value;
        setInputValue(newInputValue);

        setErrors(errors.filter((error) => error.property !== name));

        if (
            id &&
            name === 'role' &&
            value === 'Provider' &&
            getProviderData.staff.staff_role.name !== 'Provider'
        ) {
            handleCheckLastAdmin();
        }

        if (name === 'email') {
            setShowAddByEmail(false);
        }
        if (name === 'mobileNumber') {
            setShowAddByMobile(false);
        }
    };

    const onChangeAddMore = (name, index, value) => {
        let newInputValue = { ...inputValue };
        newInputValue[name][index] = value;

        setInputValue(newInputValue);

        setErrors(errors.filter((error) => error.property !== name));
    };

    const onRemoveAddMore = (name, index) => {
        const newInputValue = { ...inputValue };

        const newArr = [];
        newInputValue[name].forEach((v, i) => {
            if (i !== index) newArr.push(v);
        });
        newInputValue[name] = newArr;

        setInputValue(newInputValue);

        setErrors(errors.filter((error) => error.property !== name));
    };

    const onAddMore = (name) => {
        const newInputValue = { ...inputValue, [name]: [...inputValue[name], ''] };

        setInputValue(newInputValue);
    };

    const [inputPassword, setInputPassword] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [passwordErrors, setPasswordErrors] = useState([]);

    /*--------------- Provider Title ---------------*/
    const [titlesOptions, setTitlesOptions] = useState([]);

    const {
        data: getTitlesData,
        error: getTitlesError,
        loading: getTitlesLoading,
    } = useQuery(GET_TITLES);

    useEffect(() => {
        if (getTitlesError) {
            dispatch(alertError(`Cannot get Titles: ${getTitlesError.message}`));
        }

        if (getTitlesData && !_.isEmpty(getTitlesData.titles)) {
            const titles = getTitlesData.titles.map((item) => ({
                id: item.id,
                value: item.name,
                text: item.name,
                disabled: false,
            }));

            if (inputValue.title1 !== '') {
                titles[
                    titles.findIndex((title) => title.value === inputValue.title1)
                ].disabled = true;
            }

            if (inputValue.title2 !== '') {
                titles[
                    titles.findIndex((title) => title.value === inputValue.title2)
                ].disabled = true;
            }

            setTitlesOptions(titles);
        }
    }, [getTitlesError, getTitlesData, inputValue.title1, inputValue.title2]);

    /*--------------- Provider Position ---------------*/
    const [positionsOptions, setPositionsOptions] = useState([]);

    const {
        data: getPositionsData,
        error: getPositionsError,
        loading: getPositionsLoading,
    } = useQuery(GET_POSITIONS);

    useEffect(() => {
        if (getPositionsError) {
            dispatch(alertError(`Cannot get Positions: ${getPositionsError.message}`));
        }

        if (getPositionsData && !_.isEmpty(getPositionsData.staffPositions)) {
            const positions = getPositionsData.staffPositions.map((item) => ({
                id: item.id,
                value: item.name,
                text: item.name,
            }));

            setPositionsOptions(positions);
        }
    }, [getPositionsError, getPositionsData]);

    /*--------------- Staff Role ---------------*/
    const [rolesOptions, setRolesOptions] = useState([]);
    const {
        data: getRolesData,
        error: getRolesError,
        loading: getRolesLoading,
    } = useQuery(GET_ROLES);

    useEffect(() => {
        if (getRolesError) {
            dispatch(alertError(`Cannot get Roles: ${getRolesError.message}`));
        }
        if (getRolesData && !_.isEmpty(getRolesData.staffRoles)) {
            const roles = getRolesData.staffRoles.map((item) => ({
                id: item.id,
                value: item.name,
                text: item.name,
            }));
            setRolesOptions(roles);
        }
    }, [getRolesError, getRolesData]);

    /*--------------- Get Provider ---------------*/
    const {
        data: getProviderData,
        error: getProviderError,
        loading: getProviderLoading,
        refetch: getProviderRefetch,
    } = useQuery(GET_PROVIDER, {
        variables: { id: id },
        skip: !id || !getTitlesData,
        nextFetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (getProviderError) {
            dispatch(alertError(`Cannot get Provider: ${getProviderError.message}`));
            history.push('/provider/consultant');
            return;
        }

        if (getProviderData && getProviderData.staff === null) {
            dispatch(alertError(`Provider not found`));
            history.goBack();
            return;
        }

        if (getProviderData && getProviderData.staff) {
            handleReset();
        }
    }, [getProviderError, getProviderData]);

    /*--------------- Update Provider ---------------*/
    const [
        updateProvider,
        { data: updateProviderData, error: updateProviderError, loading: updateProviderLoading },
    ] = useMutation(UPDATE_PROVIDER);

    useEffect(() => {
        if (updateProviderError) {
            dispatch(alertError(`Cannot update Provider: ${updateProviderError.message}`));
        }
        if (updateProviderData) {
            dispatch(alertSuccess('Provider updated'));
            getProviderRefetch();

            if (user.staff.id === id) {
                dispatch(
                    fetchUser(
                        user.id,
                        undefined,
                        user.staff.staff_role.name,
                        user.staff.hospital.id
                    )
                );
            }
        }
    }, [updateProviderError, updateProviderData]);

    const handleAddTag = (value) => {
        if (id) {
            updateProvider({
                variables: {
                    userID: getProviderData.staff?.user?.id,
                    staffID: id,
                    specialist: value,
                },
            });
        } else {
            setInputValue({ ...inputValue, specialist: value });
        }
        setErrors(errors.filter((error) => error.property !== 'specialist'));
    };

    const handleReset = () => {
        if (id) {
            const {
                user: { avatar, fullname, gender, email, mobileNumber },
                deactivated,
                title1,
                title2,
                specialist,
                staff_position,
                staff_role,
                workingExperiences,
                educationCertificates,
            } = getProviderData.staff;

            setInputValue({
                avatar: avatar || '',
                title1: title1?.name || '',
                title2: title2?.name || '',
                fullname: fullname || '',
                gender: gender || '',
                specialist: specialist || [],
                position: staff_position?.name || '',
                role: staff_role?.name || '',
                email: email || '',
                mobileNumber: mobileNumber || '',
                workingExperiences: !_.isEmpty(workingExperiences)
                    ? Array.from(workingExperiences)
                    : [''],
                educationCertificates: !_.isEmpty(educationCertificates)
                    ? Array.from(educationCertificates)
                    : [''],
            });
            setDeactivated(deactivated);
            setSelectedRadio(deactivated ? 'activate' : 'deactivate');
        } else {
            setInputValue({
                ...inputValue,
                avatar: '',
                title1: '',
                title2: '',
                fullname: '',
                gender: '',
                specialist: [],
                position: '',
                role: '',
                email: '',
                mobileNumber: '',
                workingExperiences: [''],
                educationCertificates: [''],
            });
        }

        setNewAvatar(null);
        setNewFileInfo(null);
        setParentReset(parentReset + 1);

        setShowAddByEmail(false);
        setShowAddByMobile(false);
        setErrors([]);
    };

    const handleCloseDialog = () => {
        setShowDialog(false);
    };

    const makeVariables = () => {
        const variables = {
            ...inputValue,
            fullname: inputValue.fullname.trim(),
            email: inputValue.email.trim(),
            title1: getTitlesData.titles.find((tltle) => tltle.name === inputValue.title1).id,
            title2: inputValue.title2
                ? getTitlesData.titles.find((tltle) => tltle.name === inputValue.title2).id
                : null,
            staffPosition: getPositionsData.staffPositions.find(
                (position) => position.name === inputValue.position
            ).id,
            staffRole: getRolesData.staffRoles.find((role) => role.name === inputValue.role).id,
            workingExperiences: inputValue.workingExperiences
                .map((i) => i.trim())
                .filter((i) => i !== ''),
            educationCertificates: inputValue.educationCertificates
                .map((i) => i.trim())
                .filter((i) => i !== ''),
        };
        return variables;
    };

    const handleCreateAccount = async () => {
        handleCloseDialog();
        setLoading(true);
        try {
            const res = await addProviderByStaff({
                ...makeVariables(),
                hospitalID: user.staff.hospital.id,
            });

            if (!_.isNull(newAvatar)) {
                const nameArr = newFileInfo.name.split('.');
                const myFile = await getFileFromUrl(
                    newAvatar,
                    `avatar_${res.data.id}.${nameArr[nameArr.length - 1]}`,
                    newFileInfo.type
                );

                const formData = new FormData();
                formData.append('files', myFile);
                formData.append('ref', 'user');
                formData.append('refId', res.data.id);
                formData.append('field', 'avatar');
                formData.append('source', 'users-permissions');
                await uploadMedia(formData);
            }

            history.push('/provider/consultant');
            dispatch(alertSuccess('Create a new provider complete.'));
        } catch (error) {
            setLoading(false);
            dispatch(alertError(`Cannt add Provider: ${error.response?.data?.message}`));
        }
    };

    const handleAddExistUserToStaff = async (type) => {
        try {
            setLoading(true);

            const data = {
                ...makeVariables(),
                hospitalID: user.staff.hospital.id,
            };

            if (type === 'email') {
                delete data.mobileNumber;
            }
            if (type === 'mobileNumber') {
                delete data.email;
            }

            const res = await addExistUserToStaff(data);

            if (res.status === 200) {
                history.push('/provider/consultant');
            } else {
                throw new Error(res);
            }
        } catch (error) {
            setLoading(false);
            dispatch(alertError(`Add Staff to Counseling Service Error: ${error.message}`));
        }
    };

    /*--------------- Check Email exist ---------------*/
    const [showAddByEmail, setShowAddByEmail] = useState(false);
    /*--------------- Check Mobile Number exist ---------------*/
    const [showAddByMobile, setShowAddByMobile] = useState(false);

    const handleCheckExistUser = async (data) => {
        try {
            setLoading(true);
            const res = await checkExistUser(data);

            if (res.status === 200) {
                setLoading(false);

                return res.data;
            } else {
                throw new Error(res);
            }
        } catch (error) {
            setLoading(false);
            dispatch(alertError(`Check exist user: ${error.response?.data?.message}`));
        }
    };

    const handleSave = async () => {
        const newErrors = [...errors];

        if (inputValue.title1 === '') {
            newErrors.push({
                property: 'title1',
                message: 'Please provide a valid Title.',
            });
        }

        if (inputValue.fullname === '' || inputValue.fullname.trim() === '') {
            newErrors.push({
                property: 'fullname',
                message: 'Please provide a valid Fullname.',
            });
        }

        if (specialCharacters.test(inputValue.fullname)) {
            newErrors.push({
                property: 'fullname',
                message: 'Fullname must not include special characters.',
            });
        }

        if (inputValue.gender === '') {
            newErrors.push({
                property: 'gender',
                message: 'Please provide a valid gender.',
            });
        }

        if (inputValue.specialist.length === 0) {
            newErrors.push({
                property: 'specialist',
                message: 'Please provide a valid specialist.',
            });
        }

        if (inputValue.position === '') {
            newErrors.push({
                property: 'position',
                message: 'Please provide a valid Position.',
            });
        }

        if (inputValue.role === '') {
            newErrors.push({
                property: 'role',
                message: 'Please provide a valid Role.',
            });
        }

        if (!validEmail.test(inputValue.email)) {
            newErrors.push({
                property: 'email',
                message: 'Please provide a valid Email.',
            });
        } else {
            setShowAddByEmail(false);

            const checkEmailRes = await handleCheckExistUser({
                email: inputValue.email,
                userID: id ? getProviderData?.staff?.user?.id : undefined,
                role: 'staff',
                hospital: user.staff.hospital.id,
            });

            switch (checkEmailRes) {
                case 'userNotFound':
                    break;
                case 'isThisUser':
                    break;
                case 'userFound':
                    newErrors.push({
                        property: 'email',
                        message: 'Email already exists.',
                    });
                    if (!id) {
                        setShowAddByEmail(true);
                    }
                    break;
                case 'staffFound':
                    newErrors.push({
                        property: 'email',
                        message: 'Staff already in Counseling Service.',
                    });
                    break;
            }
        }

        if (!validPhone.test(inputValue.mobileNumber)) {
            newErrors.push({
                property: 'mobileNumber',
                message: 'Please provide a valid Mobile Number.',
            });
        } else {
            setShowAddByMobile(false);

            const checkMobileRes = await handleCheckExistUser({
                mobileNumber: inputValue.mobileNumber,
                userID: id ? getProviderData?.staff?.user?.id : undefined,
                role: 'staff',
                hospital: user?.staff?.hospital.id,
            });

            switch (checkMobileRes) {
                case 'userNotFound':
                    break;
                case 'isThisUser':
                    break;
                case 'userFound':
                    newErrors.push({
                        property: 'mobileNumber',
                        message: 'Mobile Number already exists.',
                    });
                    if (!id) {
                        setShowAddByMobile(true);
                    }
                    break;
                case 'staffFound':
                    newErrors.push({
                        property: 'mobileNumber',
                        message: 'Staff already in Hospital.',
                    });
                    break;
            }
        }

        if (!_.isEmpty(newErrors)) return setErrors(newErrors);

        if (!id) {
            return setShowDialog(true);
        }

        if (!_.isNull(newAvatar)) {
            uploadAvatar();
        }

        updateProvider({
            variables: {
                ...makeVariables(),
                userID: getProviderData.staff?.user?.id,
                staffID: id,
            },
        });
    };

    /*--------------- Update User Avatar ---------------*/
    const [newAvatar, setNewAvatar] = useState(null);
    const [newFileInfo, setNewFileInfo] = useState(null);

    const [deleteUserAvatar, { error: deleteUserAvatarError }] = useMutation(DELETE_USER_AVATAR);

    useEffect(() => {
        if (deleteUserAvatarError) {
            dispatch(alertError(`Cannot delete user avatar: ${deleteUserAvatarError.message}`));
        }
    }, [deleteUserAvatarError]);

    const uploadAvatar = async () => {
        const nameArr = newFileInfo.name.split('.');

        const myFile = await getFileFromUrl(
            newAvatar,
            `avatar_${getProviderData.staff?.user?.id}.${nameArr[nameArr.length - 1]}`,
            newFileInfo.type
        );

        const formData = new FormData();
        formData.append('files', myFile);
        formData.append('ref', 'user');
        formData.append('refId', getProviderData.staff?.user?.id);
        formData.append('field', 'avatar');
        formData.append('source', 'users-permissions');

        if (!_.isNull(getProviderData.staff?.user?.avatar)) {
            try {
                deleteUserAvatar({
                    variables: {
                        id: getProviderData.staff?.user?.id,
                    },
                });
                await deleteMedia(getProviderData.staff?.user?.avatar.id);
            } catch (error) {
                console.log('deleteMedia error', error);
            }
        }

        try {
            const res = await uploadMedia(formData);

            if (!_.isEmpty(res)) {
                setNewAvatar(null);
                setNewFileInfo(null);
                dispatch(alertSuccess('Avatar Uploaded'));

                getProviderRefetch();
                if (user.staff.id === getProviderData.staff.id) {
                    dispatch(
                        fetchUser(
                            user.id,
                            undefined,
                            user.staff.staff_role.name,
                            user.staff.hospital.id
                        )
                    );
                }
            }
        } catch (error) {
            dispatch(
                alertError(`Cannot upload Avatar: ${JSON.stringify(error.response?.data?.message)}`)
            );
        }
    };

    const handleInputPasswordChange = (e) => {
        const newInputPassword = { ...inputPassword };
        const value = e.target.value;
        const name = e.target.name;
        newInputPassword[name] = value;
        setInputPassword(newInputPassword);

        setPasswordErrors(passwordErrors.filter((error) => error.property !== name));
    };

    const handleClearInputPassword = () => {
        setInputPassword({ oldPassword: '', newPassword: '', confirmPassword: '' });
    };

    const handleResetPassword = async () => {
        try {
            const newErrors = [];
            const oldPassword = inputPassword.oldPassword;
            const newPassword = inputPassword.newPassword;
            const confirmPassword = inputPassword.confirmPassword;

            if (!oldPassword) {
                newErrors.push({
                    property: 'oldPassword',
                    message: 'Please provide a valid Old password.',
                });
            }
            if (!newPassword) {
                newErrors.push({
                    property: 'newPassword',
                    message: 'Please provide a valid New password.',
                });
            }
            if (!validPassword.test(newPassword)) {
                newErrors.push({
                    property: 'newPassword',
                    message:
                        'Password must be at least 8 character and special character and number.',
                });
            }
            if (oldPassword == newPassword) {
                newErrors.push({
                    property: 'newPassword',
                    message: 'New password must differ from old password.',
                });
            }

            if (!confirmPassword) {
                newErrors.push({
                    property: 'confirmPassword',
                    message: 'Please provide a valid Confirm new password.',
                });
            }

            if (newPassword !== confirmPassword) {
                newErrors.push({
                    property: 'confirmPassword',
                    message: 'New password and confirm password must be equal.',
                });
            }

            if (!_.isEmpty(newErrors)) return setPasswordErrors(newErrors);

            setLoading(true);

            const res = await resetPassword({
                oldPassword: oldPassword,
                newPassword: newPassword,
                user_id: user.id,
                notificationOwner: user.staff.id,
            });
            if (!res) {
                throw new Error(res);
            }
            dispatch(alertSuccess(res));
            setPasswordErrors([]);
            setInputPassword({ oldPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            dispatch(alertError(error.response?.data?.message));
        }
        setLoading(false);
    };

    /*--------------- activate deactivate delete staff ---------------*/
    const [open, setOpen] = useState(false);
    const [selectedRadio, setSelectedRadio] = useState(deactivated ? 'activate' : 'deactivate');

    const handleRadioChange = (value) => {
        setSelectedRadio(value);
    };
    const onClickDeactivate = () => {
        setOpen(true);
    };
    const onClickCancel = () => {
        setOpen(false);
    };
    const onClickConfirm = async () => {
        setOpen(false);
        setLoading(true);

        try {
            if (selectedRadio !== 'activate') {
                const checkRes = await checkLastAdmin({
                    hospitalID: getProviderData.staff.hospital.id,
                    staffID: getProviderData.staff.id,
                });

                if (checkRes.status === 200) {
                    if (checkRes.data.canChange === false) {
                        dispatch(
                            alertError(
                                `Cannot ${
                                    selectedRadio[0].toLocaleUpperCase() + selectedRadio.slice(1)
                                } Last Admin`
                            )
                        );
                        setLoading(false);
                        return;
                    }
                } else {
                    throw new Error(checkRes);
                }
            }

            let res;

            const data = {
                id,
                deactivate: !deactivated,
                hospital: getProviderData.staff.hospital.id,
            };

            if (selectedRadio === 'delete') {
                res = await deleteStaff(data);
            } else {
                res = await deactivateStaff(data);
            }

            if (res.status === 200) {
                if (user.staff.id === id && selectedRadio !== 'activate') {
                    window.location.reload();
                }
                getProviderRefetch();
            } else {
                throw new Error(res);
            }
        } catch (error) {
            dispatch(alertError(error.response?.data?.message));
        }
        setLoading(false);
    };

    return (
        <div
            style={{
                marginTop: '20px',
                display: 'flex',
                flexDirection: 'column',
                rowGap: '15px',
                position: 'relative',
            }}
        >
            {(loading ||
                getTitlesLoading ||
                getPositionsLoading ||
                getRolesLoading ||
                getProviderLoading ||
                updateProviderLoading) && <Progress />}
            <ConfirmAccountDialog
                open={showDialog}
                onClose={handleCloseDialog}
                onConfirm={handleCreateAccount}
            />
            <StyledCard style={{ padding: '24px 30px' }}>
                <StyledCardContent
                    style={{ display: 'flex', flexDirection: 'column', rowGap: '20px' }}
                >
                    <Text $fontSize="18px" $bold>
                        Personal Information
                    </Text>
                    <div style={{ display: 'flex', columnGap: '35px' }}>
                        <div>
                            <AvatarWithUploader
                                alt={`${inputValue?.fullname}`}
                                src={inputValue.avatar ? inputValue.avatar.url : null}
                                $avatarSize="120px"
                                $borderRadius="50%"
                                setNewAvatar={setNewAvatar}
                                setNewFileInfo={setNewFileInfo}
                                parentReset={parentReset}
                            />
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                rowGap: '5px',
                                flexGrow: 1,
                            }}
                        >
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2,130px)',
                                    columnGap: '15px',
                                }}
                            >
                                <Select
                                    label="Title"
                                    required
                                    name="title1"
                                    value={inputValue?.title1}
                                    onChange={handleChange}
                                    options={titlesOptions}
                                    placeholder="Title"
                                    deselect
                                    disabled={getTitlesError}
                                />
                                <Select
                                    label="Title (optional)"
                                    name="title2"
                                    value={inputValue?.title2}
                                    onChange={handleChange}
                                    options={titlesOptions}
                                    placeholder="Title"
                                    deselect
                                    disabled={getTitlesError}
                                />
                                <div style={{ gridColumn: 'span 2' }}>
                                    {errors.find((error) => error.property === 'title1') && (
                                        <StyledFormHelperText error>
                                            {
                                                errors.find((error) => error.property === 'title1')
                                                    ?.message
                                            }
                                        </StyledFormHelperText>
                                    )}
                                </div>
                            </div>
                            <div style={{ maxWidth: '410px' }}>
                                <InputWithEndAdornment
                                    id="fullname"
                                    name="fullname"
                                    type="text"
                                    label="Fullname"
                                    placeholder="Fullname"
                                    required
                                    onChange={handleChange}
                                    value={inputValue.fullname}
                                    helperText={
                                        errors.find((error) => error.property === 'fullname')
                                            ?.message
                                    }
                                    inputProps={{ maxLength: 100 }}
                                />
                            </div>
                            <RadioGroup
                                label="Gender"
                                name="gender"
                                value={inputValue.gender}
                                onChange={handleChange}
                                required
                                helperText={
                                    errors.find((error) => error.property === 'gender')?.message
                                }
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        columnGap: '10px',
                                    }}
                                >
                                    <Radio value="male" label="Male" />
                                    <Radio value="female" label="Female" />
                                    <Radio value="LGBTIQN+" label="LGBTIQN+" />
                                    <Radio value="other" label="Prefer not to say" />
                                </div>
                            </RadioGroup>
                            <TagAdd
                                label="Specialist"
                                required
                                data={inputValue.specialist}
                                onAddTag={handleAddTag}
                                helperText={
                                    errors.find((error) => error.property === 'specialist')?.message
                                }
                            />
                            <div style={{ maxWidth: '330px' }}>
                                <Select
                                    label="Position"
                                    required
                                    name="position"
                                    value={inputValue.position}
                                    onChange={handleChange}
                                    options={positionsOptions}
                                    placeholder="Position"
                                    helperText={
                                        errors.find((error) => error.property === 'position')
                                            ?.message
                                    }
                                />
                            </div>
                            {!_.isEmpty(user) &&
                                (user.staff.staff_role.name === 'Admin' ||
                                    user.staff.staff_role.name === 'Provider Admin') && (
                                    <div style={{ maxWidth: '330px' }}>
                                        <Select
                                            label="Role"
                                            required
                                            name="role"
                                            value={inputValue.role}
                                            onChange={handleChange}
                                            options={rolesOptions}
                                            placeholder="Role"
                                            helperText={
                                                errors.find((error) => error.property === 'role')
                                                    ?.message
                                            }
                                        />
                                    </div>
                                )}
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2,280px)',
                                    columnGap: '15px',
                                }}
                            >
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <Input
                                        id="email"
                                        label="Mail"
                                        placeholder="Mail"
                                        name="email"
                                        type="email"
                                        value={inputValue.email}
                                        onChange={handleChange}
                                        required
                                        noPublic
                                        helperText={
                                            errors.find((error) => error.property === 'email')
                                                ?.message
                                        }
                                        max={50}
                                    />
                                    {showAddByEmail && (
                                        <ContainedButton
                                            $fontSize="12px"
                                            style={{
                                                paddingLeft: '20px',
                                                height: '32px',
                                                width: 'max-content',
                                            }}
                                            onClick={() => handleAddExistUserToStaff('email')}
                                        >
                                            Add Staff To Counseling Service
                                        </ContainedButton>
                                    )}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <Input
                                        id="phone"
                                        label="Mobile number"
                                        placeholder="Mobile number"
                                        name="mobileNumber"
                                        value={inputValue.mobileNumber}
                                        onChange={handleChange}
                                        required
                                        InputProps={{
                                            inputComponent: PhoneFormat,
                                        }}
                                        helperText={
                                            errors.find(
                                                (error) => error.property === 'mobileNumber'
                                            )?.message
                                        }
                                        noPublic
                                    />
                                    {showAddByMobile && (
                                        <ContainedButton
                                            $fontSize="12px"
                                            style={{
                                                paddingLeft: '20px',
                                                height: '32px',
                                                width: 'max-content',
                                            }}
                                            onClick={() =>
                                                handleAddExistUserToStaff('mobileNumber')
                                            }
                                        >
                                            Add Staff To Counseling Service
                                        </ContainedButton>
                                    )}
                                </div>
                            </div>

                            {user.staff.id === id && (
                                <Fragment>
                                    <div style={{ maxWidth: '575px', marginTop: '10px' }}>
                                        <AddMoreInput
                                            name="workingExperiences"
                                            label="Working Experiences"
                                            placeholder="Working Experiences"
                                            values={inputValue.workingExperiences}
                                            onChange={onChangeAddMore}
                                            onRemove={onRemoveAddMore}
                                            onAddMore={onAddMore}
                                        />
                                    </div>
                                    <div style={{ maxWidth: '575px', marginTop: '10px' }}>
                                        <AddMoreInput
                                            name="educationCertificates"
                                            label="Education and Certificates"
                                            placeholder="Education and Certificates"
                                            values={inputValue.educationCertificates}
                                            onChange={onChangeAddMore}
                                            onRemove={onRemoveAddMore}
                                            onAddMore={onAddMore}
                                        />
                                    </div>
                                </Fragment>
                            )}
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: 'flex-end',
                                    columnGap: '15px',
                                    flexGrow: 1,
                                    marginTop: '5px',
                                }}
                            >
                                <OutlinedButton
                                    style={{ height: '32px', width: '93px' }}
                                    onClick={handleReset}
                                    $noShadow
                                    disabled={
                                        loading ||
                                        getTitlesLoading ||
                                        getPositionsLoading ||
                                        getRolesLoading ||
                                        getProviderLoading ||
                                        updateProviderLoading
                                    }
                                >
                                    Reset
                                </OutlinedButton>
                                <ContainedButton
                                    style={{ height: '32px', width: '93px' }}
                                    onClick={handleSave}
                                    $noShadow
                                    disabled={!_.isEmpty(errors)}
                                    loading={
                                        loading ||
                                        getTitlesLoading ||
                                        getPositionsLoading ||
                                        getRolesLoading ||
                                        getProviderLoading ||
                                        updateProviderLoading
                                    }
                                >
                                    {id ? 'Save' : 'Create'}
                                </ContainedButton>
                            </div>
                        </div>
                    </div>
                </StyledCardContent>
            </StyledCard>
            {user.staff.id === id && (
                <StyledCard style={{ padding: '24px 30px' }}>
                    <StyledCardContent
                        style={{ display: 'flex', flexDirection: 'column', rowGap: '10px' }}
                    >
                        <Text $fontSize="18px" $bold>
                            Password Change
                        </Text>
                        <div style={{ maxWidth: '410px' }}>
                            <PasswordInput
                                id="oldPassword"
                                name="oldPassword"
                                type="text"
                                label="Old password"
                                placeholder="Please input your old password"
                                onChange={handleInputPasswordChange}
                                value={inputPassword.oldPassword}
                                helperText={
                                    passwordErrors.find((error) => error.property === 'oldPassword')
                                        ?.message
                                }
                            />
                        </div>
                        <div style={{ maxWidth: '410px' }}>
                            <PasswordInput
                                id="newPassword"
                                name="newPassword"
                                type="text"
                                label="New password"
                                placeholder="Please input your new password"
                                onChange={handleInputPasswordChange}
                                value={inputPassword.newPassword}
                                helperText={
                                    passwordErrors.find((error) => error.property === 'newPassword')
                                        ?.message
                                }
                            />
                        </div>
                        <div style={{ maxWidth: '410px' }}>
                            <PasswordInput
                                id="confirmPassword"
                                name="confirmPassword"
                                type="text"
                                label="Confirm new password"
                                placeholder="Please confirm your new password"
                                onChange={handleInputPasswordChange}
                                value={inputPassword.confirmPassword}
                                helperText={
                                    passwordErrors.find(
                                        (error) => error.property === 'confirmPassword'
                                    )?.message
                                }
                            />
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'flex-end',
                                columnGap: '15px',
                                flexGrow: 1,
                                marginTop: '5px',
                            }}
                        >
                            <OutlinedButton
                                style={{ height: '32px', width: '93px' }}
                                onClick={handleClearInputPassword}
                                $noShadow
                                disabled={
                                    loading ||
                                    getTitlesLoading ||
                                    getPositionsLoading ||
                                    getRolesLoading ||
                                    getProviderLoading ||
                                    updateProviderLoading
                                }
                            >
                                Reset
                            </OutlinedButton>
                            <ContainedButton
                                style={{ height: '32px', width: '93px' }}
                                onClick={handleResetPassword}
                                $noShadow
                                disabled={
                                    loading ||
                                    getTitlesLoading ||
                                    getPositionsLoading ||
                                    getRolesLoading ||
                                    getProviderLoading ||
                                    updateProviderLoading
                                }
                            >
                                Save
                            </ContainedButton>
                        </div>
                    </StyledCardContent>
                </StyledCard>
            )}

            {id && ['Provider Admin', 'Admin'].includes(user.staff.staff_role.name) && (
                <Fragment>
                    <ConfirmDialog
                        open={open}
                        contentText={`Are you sure you want to ${selectedRadio} staff ?`}
                        cancelText="Cancel"
                        onCancel={onClickCancel}
                        confirmText="Confirm"
                        onConfirm={onClickConfirm}
                    />
                    <StyledCard style={{ padding: '24px 30px' }}>
                        <StyledCardContent
                            style={{ display: 'flex', flexDirection: 'column', rowGap: '10px' }}
                        >
                            <Text $fontSize="18px" $bold>
                                {`${deactivated ? 'Activate' : 'Deactivate'} or delete account`}
                            </Text>
                            <Text $fontSize="14px" $color="gray2">
                                If you want to take a break from Friday, you can deactivate your
                                account. If you want to permanently delete account, let us know.{' '}
                            </Text>
                            <div
                                style={{ display: 'flex', flexDirection: 'column', rowGap: '20px' }}
                            >
                                <RadioCard
                                    label={deactivated ? 'Activate account' : 'Deactivate account'}
                                    subtitle={
                                        deactivated
                                            ? 'This will enable the account to access services on FRIDAY.'
                                            : 'Temporarily deactivating the account.'
                                    }
                                    content={
                                        deactivated
                                            ? ''
                                            : 'This will disable all functions of this account on FRIDAY temporary.'
                                    }
                                    value={deactivated ? 'activate' : 'deactivate'}
                                    handleChange={handleRadioChange}
                                    selectedValue={selectedRadio}
                                />
                                <RadioCard
                                    label="Delete account"
                                    subtitle="Permanently deleting the account."
                                    content="This will delete everything associated with this account, and it is not possible to retrieve any information from this account on FRIDAY."
                                    value={'delete'}
                                    handleChange={handleRadioChange}
                                    selectedValue={selectedRadio}
                                />
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: 'flex-end',
                                    columnGap: '15px',
                                    flexGrow: 1,
                                    marginTop: '5px',
                                }}
                            >
                                <ContainedButton
                                    style={{ height: '32px', width: 'auto' }}
                                    onClick={onClickDeactivate}
                                    $noShadow
                                    disabled={
                                        loading ||
                                        getTitlesLoading ||
                                        getPositionsLoading ||
                                        getRolesLoading ||
                                        getProviderLoading ||
                                        updateProviderLoading
                                    }
                                >
                                    {`Continue to Account ${
                                        selectedRadio[0].toLocaleUpperCase() +
                                        selectedRadio.slice(1)
                                    }`}
                                </ContainedButton>
                            </div>
                        </StyledCardContent>
                    </StyledCard>
                </Fragment>
            )}
        </div>
    );
};

export default PersonalProfile;
